import React from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { captureReactBoundaryErrorTelemetry } from '@/telemetry/errorSink';

interface RootErrorBoundaryProps {
  children: React.ReactNode;
}

interface RootErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

const INITIAL_STATE: RootErrorBoundaryState = {
  hasError: false,
  error: null,
  componentStack: null,
};

function getObjectStringValue(obj: unknown, key: string): string | null {
  if (!obj || typeof obj !== 'object') {
    return null;
  }
  const raw = (obj as Record<string, unknown>)[key];
  if (typeof raw !== 'string') {
    return null;
  }
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function extractRequestId(error: Error | null): string | null {
  if (!error) {
    return null;
  }
  return getObjectStringValue(error, 'request_id') || getObjectStringValue(error, 'requestId');
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M12 3L2 20h20L12 3z" stroke="currentColor" strokeWidth="1.5" />
      <path d="M12 9v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="12" cy="17" r="1" fill="currentColor" />
    </svg>
  );
}

function RootErrorFallback({
  error,
  componentStack,
  onRetry,
}: {
  error: Error | null;
  componentStack: string | null;
  onRetry: () => void;
}) {
  const showDiagnostics = import.meta.env.DEV || import.meta.env.MODE === 'test';
  const requestId = extractRequestId(error);
  const diagnostics = [
    `route=${window.location.pathname}${window.location.search}${window.location.hash}`,
    `message=${error?.message || 'Unknown render error'}`,
    error?.stack ? `stack=${error.stack}` : null,
    componentStack ? `component_stack=${componentStack}` : null,
  ]
    .filter(Boolean)
    .join('\n');

  return (
    <div className="relative flex min-h-dvh items-center justify-center overflow-hidden bg-background px-5 py-8">
      <div
        className={cn(
          'pointer-events-none absolute inset-0',
          'bg-[radial-gradient(circle_at_15%_20%,hsl(var(--color-muted))_0%,transparent_40%),radial-gradient(circle_at_85%_80%,hsl(var(--color-border))_0%,transparent_35%)]',
        )}
      />

      <section
        role="alert"
        data-testid="root-error-screen"
        className={cn(
          'relative w-full max-w-2xl rounded-2xl border border-border bg-card/95 p-6 shadow-[0_24px_64px_-32px_rgba(15,23,42,0.35)]',
          'sm:p-8',
        )}
      >
        <div className="flex items-start gap-4">
          <div className="rounded-xl border border-border bg-muted/70 p-2 text-destructive">
            <WarningIcon className="size-5" />
          </div>
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">
              System Error
            </p>
            <h1 className="text-balance text-2xl font-semibold text-foreground">
              Приложение столкнулось с ошибкой
            </h1>
            <p className="text-pretty text-sm text-muted-foreground">
              Мы уже зафиксировали проблему. Обновите страницу или откройте главный раздел, чтобы
              продолжить работу.
            </p>
          </div>
        </div>

        {requestId ? (
          <div className="mt-5 rounded-lg border border-border bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
            Код запроса:{' '}
            <span className="font-mono text-foreground" data-testid="root-error-request-id">
              {requestId}
            </span>
          </div>
        ) : null}

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Button onClick={() => window.location.reload()}>Обновить страницу</Button>
          <Button variant="secondary" onClick={onRetry}>
            Попробовать снова
          </Button>
          <Button variant="ghost" onClick={() => window.location.assign('/webadmin/app/invoices')}>
            На главную
          </Button>
        </div>

        {showDiagnostics ? (
          <details className="mt-6 rounded-lg border border-border bg-muted/40 p-3">
            <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
              Технические детали (dev/test)
            </summary>
            <pre className="mt-3 overflow-x-auto whitespace-pre-wrap break-words font-mono text-xs text-foreground">
              {diagnostics}
            </pre>
          </details>
        ) : null}
      </section>
    </div>
  );
}

/**
 * Root-level React error boundary for render/runtime crashes outside router `errorElement`.
 */
export class RootErrorBoundary extends React.Component<
  RootErrorBoundaryProps,
  RootErrorBoundaryState
> {
  state: RootErrorBoundaryState = INITIAL_STATE;
  private lastCaptureSignature: string | null = null;

  static getDerivedStateFromError(error: Error): Partial<RootErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    const signature = `${error.name}:${error.message}:${error.stack || ''}:${info.componentStack || ''}`;
    if (this.lastCaptureSignature !== signature) {
      this.lastCaptureSignature = signature;
      const telemetryContext = {
        component: 'root.errorBoundary',
        ...(info.componentStack ? { componentStack: info.componentStack } : {}),
      };
      captureReactBoundaryErrorTelemetry(error, {
        ...telemetryContext,
      });
    }

    this.setState({
      componentStack: info.componentStack || null,
    });
  }

  private handleRetry = (): void => {
    this.lastCaptureSignature = null;
    this.setState(INITIAL_STATE);
  };

  render() {
    if (this.state.hasError) {
      return (
        <RootErrorFallback
          error={this.state.error}
          componentStack={this.state.componentStack}
          onRetry={this.handleRetry}
        />
      );
    }

    return this.props.children;
  }
}
